import { Icon } from "@outschool/backpack";
import { ShareContainer_ActivityFragment } from "@outschool/gql-frontend-generated";
import { faFacebook, faFacebookMessenger } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import {
  ExternalLink,
  FacebookButton,
  FacebookButtonProps
} from "@outschool/ui-components-shared";
import { useNavigation } from "@outschool/ui-utils";
import React from "react";

import {
  facebookActivitySendUrl,
  facebookActivityShareUrl,
  facebookGeneralShareUrl,
  facebookLeaderShareUrl,
  facebookParentShareUrl
} from "../../../shared/Sharing";
import { EnrolledClassSection } from "../enrolled/EnrolledClassTypes";
import VerticallyAlignedIconButtonAndLabel from "./VerticallyAlignedIconButtonAndLabel";

/**
 * This module provides various interaction elements to share entities on Facebook.
 * Pay extra attention to the difference between Feed and Share Dialogs. Check the
 * related documentation linked for each item.
 *
 * Basically Share Dialog can add a quote before the shared element but has no control
 * on the shared entity card while Feed Dialog has control over the image, title, etc of
 * shared entity card but can't add extra quotes
 */

interface Referrer {
  slug_id: string;
}

interface OptionalLabelAndReferrer {
  label?: string;
  referrer?: Referrer;
}

type ActivityShareDialogProps = OptionalLabelAndReferrer & {
  activity: ShareContainer_ActivityFragment;
  section?: EnrolledClassSection;
  quote?: string;
  campaignPrefix?: string;
  segmentEventName: string;
  verticallyAlignButtonAndLabel?: boolean;
} & FacebookButtonProps;

// See https://developers.facebook.com/docs/sharing/reference/share-dialog
export function ActivityShareDialog({
  label,
  activity,
  section,
  quote,
  segmentEventName,
  campaignPrefix,
  referrer,
  verticallyAlignButtonAndLabel = false,
  ...props
}: ActivityShareDialogProps) {
  const { t } = useTranslation("client\\components\\sharing\\Facebook");
  const shareOnFacebook = t("Share on Facebook");
  const navigate = useNavigation();
  const trackEvent = useTrackEvent();
  const shareUrl = facebookActivityShareUrl(
    activity,
    section,
    referrer,
    quote,
    campaignPrefix
  );

  const onClick = React.useCallback(() => {
    trackEvent(segmentEventName, {
      activity_uid: activity?.uid,
      section_uid: section?.uid
    });
    navigate(shareUrl, { newTab: true });
  }, [activity, section, shareUrl, segmentEventName, trackEvent, navigate]);

  if (verticallyAlignButtonAndLabel) {
    return (
      <VerticallyAlignedIconButtonAndLabel
        onClick={onClick}
        icon={faFacebook}
        label={t("Facebook")}
      />
    );
  }

  return (
    <FacebookButton
      onClick={onClick}
      title={shareOnFacebook}
      startIcon={
        <Icon
          icon={faFacebook}
          sx={{
            fontSize: "1.333em"
          }}
        />
      }
      {...props}
    >
      {label || shareOnFacebook}
    </FacebookButton>
  );
}
ActivityShareDialog.displayName = "ActivityShareDialog";

type ActivitySendDialogProps = OptionalLabelAndReferrer & {
  activity: ShareContainer_ActivityFragment;
  section?: EnrolledClassSection;
  campaignPrefix?: string;
  segmentEventName?: string;
} & FacebookButtonProps;

// See https://developers.facebook.com/docs/sharing/reference/send-dialog
export function ActivitySendDialog({
  label,
  activity,
  section,
  segmentEventName = "share_to_messenger",
  campaignPrefix,
  referrer,
  ...props
}: ActivitySendDialogProps) {
  const { t } = useTranslation("client\\components\\sharing\\Facebook");
  const shareWithMessenger = t("Share with Messenger");
  const navigate = useNavigation();
  const shareUrl = facebookActivitySendUrl(
    activity,
    section,
    referrer,
    campaignPrefix
  );
  return (
    <FacebookButton
      onClick={() => navigate(shareUrl, { newTab: true })}
      title={shareWithMessenger}
      startIcon={
        <Icon
          icon={faFacebookMessenger}
          sx={{
            fontSize: "1.333em"
          }}
        />
      }
      trackingName={segmentEventName}
      trackingUniqueId={activity.uid}
      {...props}
    >
      {label || shareWithMessenger}
    </FacebookButton>
  );
}
ActivitySendDialog.displayName = "ActivitySendDialog";

type GeneralShareDialogProps = {
  url: string;
  label?: string;
} & FacebookButtonProps;

export const GeneralShareDialog = ({
  url,
  label,
  sx
}: GeneralShareDialogProps) => {
  const { t } = useTranslation("client\\components\\sharing\\Facebook");
  const navigate = useNavigation();
  const shareUrl = facebookGeneralShareUrl(url);
  const shareOnFacebook = t("Share on Facebook");
  return (
    <FacebookButton
      sx={[{ marginBottom: "1em" }, ...(Array.isArray(sx) ? sx : [sx])]}
      fullWidth
      onClick={() => {
        navigate(shareUrl, { newTab: true });
      }}
      title={shareOnFacebook}
      trackingName="share_via_facebook"
      startIcon={
        <Icon
          icon={faFacebook}
          sx={{
            fontSize: "1.333em"
          }}
        />
      }
    >
      {label || shareOnFacebook}
    </FacebookButton>
  );
};

type TeacherFeedDialogProps = OptionalLabelAndReferrer & {
  leader: { uid: string; leader_link?: string | null };
} & FacebookButtonProps;

export const TeacherFeedDialog = ({
  label,
  leader,
  referrer,
  ...props
}: TeacherFeedDialogProps) => {
  const trackEvent = useTrackEvent();
  const { t } = useTranslation("client\\components\\sharing\\Facebook");
  const shareUrl = facebookLeaderShareUrl(leader, referrer);
  const shareOnFacebook = t("Share on Facebook");

  return (
    <FacebookButton
      component={ExternalLink}
      url={shareUrl}
      onClick={() =>
        trackEvent("Share leader to Facebook clicked", {
          leader_uid: leader.uid
        })
      }
      title={shareOnFacebook}
      {...props}
    >
      {label || shareOnFacebook}
    </FacebookButton>
  );
};

type ParentFeedDialogProps = OptionalLabelAndReferrer & {
  parent: { uid: string };
} & FacebookButtonProps;

export function ParentFeedDialog({
  label,
  parent,
  referrer,
  ...props
}: ParentFeedDialogProps) {
  const trackEvent = useTrackEvent();
  const { t } = useTranslation("client\\components\\sharing\\Facebook");
  const shareUrl = facebookParentShareUrl(parent, referrer);
  const shareOnFacebook = t("Share on Facebook");
  return (
    <FacebookButton
      component={ExternalLink}
      url={shareUrl}
      onClick={() =>
        trackEvent("Share parent to Facebook clicked", {
          parent_uid: parent.uid
        })
      }
      title={shareOnFacebook}
      {...props}
    >
      {label || shareOnFacebook}
    </FacebookButton>
  );
}
